import React, { useEffect, useId, useRef, useState } from 'react';
import './RecordCollectionCard.css';
import { getLockedRecordArtwork } from '../services/RecordArtwork';
import { t } from '../i18n';
import { RECORD_TIER_NAMES } from '../utils/recordAchievements';
import useCardFlipAudio from '../hooks/useCardFlipAudio';

export default function RecordCollectionCard({
  artwork,
  number,
  series = 'cumulative',
  label,
  value,
  targetValue,
  valueVariant,
  isUnlocked = true,
  tier = 0,
  tierTargets,
  fallbackIcon: FallbackIcon,
  className = '',
}) {
  const [isFlipped, setIsFlipped] = useState(false);
  const playCardFlip = useCardFlipAudio();
  const [hasRevealed, setHasRevealed] = useState(false);
  const cardRef = useRef(null);
  const detailsId = useId();
  const tierName = tierTargets?.length && tier > 0 ? RECORD_TIER_NAMES[tier - 1] : null;
  const frontTarget = tierTargets?.[Math.max(0, tier - 1)] ?? targetValue;
  const text = (key) => t(`accountActivity.recordCard.${key}`);
  const displayedArtwork = isUnlocked ? artwork : getLockedRecordArtwork(artwork);
  useEffect(() => {
    if (!isFlipped) return undefined;
    const dismissOutside = (event) => {
      if (!cardRef.current?.contains(event.target)) setIsFlipped(false);
    };
    const dismissEscape = (event) => { if (event.key === 'Escape') setIsFlipped(false); };
    document.addEventListener('pointerdown', dismissOutside);
    document.addEventListener('keydown', dismissEscape);
    return () => {
      document.removeEventListener('pointerdown', dismissOutside);
      document.removeEventListener('keydown', dismissEscape);
    };
  }, [isFlipped]);
  return (
    <article ref={cardRef} className={`record-collection-card record-collection-card--${series} ${isUnlocked ? 'is-unlocked' : 'is-locked'}${tierName ? ` record-tier-${tierName}` : ''}${isFlipped ? ' is-flipped' : ''} ${className}`}>
      <div className="record-collection-card-inner">
        <div className="record-collection-card-front" aria-hidden={isFlipped}>
          <div className={`record-collection-card-artwork${isUnlocked ? '' : ' is-locked'}`}>
            {displayedArtwork ? (
              <img src={displayedArtwork} alt="" loading="lazy" decoding="async" width="600" height="600" />
            ) : (
              <div className="record-collection-card-placeholder" aria-hidden="true">
                {FallbackIcon ? <FallbackIcon size={52} strokeWidth={1.7} /> : null}
              </div>
            )}
            <span className="record-collection-card-number" aria-hidden="true">
              {String(number).padStart(2, '0')}
            </span>
            {tierName && <span className="record-collection-card-tier">
              <span aria-hidden="true">{'◆'.repeat(tier)}</span> {text(tierName)}
            </span>}
          </div>
          <div className="record-collection-card-details">
            <span className="record-collection-card-label">{label}</span>
            <strong className={`record-collection-card-value${valueVariant ? ` is-${valueVariant}` : ''}`}>
              {frontTarget ?? value}
            </strong>
          </div>
        </div>
        <div id={detailsId} className="record-collection-card-back" aria-hidden={!isFlipped}>
          {hasRevealed && <>
            <span className="record-collection-card-back-title">{label}</span>
            <div className="record-collection-card-current">
              <span>{text('current')}</span>
              <strong>{value}</strong>
            </div>
            <ul className="record-collection-card-targets" aria-label={text('targets')}>
              {(tierTargets || [targetValue]).map((target, index) => {
                const earned = tierTargets ? tier > index : isUnlocked;
                return <li key={index} className={earned ? 'is-earned' : ''}>
                  <span className="record-collection-card-target-label">
                    <span aria-hidden="true">{earned ? '✓' : '◇'}</span>
                    {text(tierTargets ? RECORD_TIER_NAMES[index] : 'target')}
                    <span className="record-collection-card-sr-only">{text(earned ? 'earned' : 'locked')}</span>
                  </span>
                  <strong>{target}</strong>
                </li>;
              })}
            </ul>
            <span className="record-collection-card-hint">{text('flipBack')}</span>
          </>}
        </div>
      </div>
      {frontTarget != null && <button type="button" className="record-collection-card-trigger"
        aria-label={`${label} ${frontTarget}`} aria-expanded={isFlipped}
        aria-controls={detailsId} aria-describedby={isFlipped ? detailsId : undefined}
        onClick={() => { playCardFlip(); setHasRevealed(true); setIsFlipped((visible) => !visible); }} />}
    </article>
  );
}
